import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { advancedHostingActions } from '@/redux/modules/advancedHosting';
import { VpsOciStatusEnum } from '@/pages/Products/products.enum';
import { HgProducts } from '@/components/SupportForms/SupportForms.types';
import { IS_PROD } from '@/config';

/**
 * Hook que busca e retorna o status formatado de um servidor específico.
 * Cada servidor tem seu próprio estado no Redux, evitando sobrescrita.
 * Funciona tanto na listagem quanto na página de detalhes.
 *
 * @param {Object} serverInfo - Informações do servidor, usadas para status locked/pending/suspended/building/offline
 * @param {string|number} serverId - ID do servidor / ID do Hosting
 * @returns {{
 *   status: { value: string, variant: string } | null,
 *   loading: boolean,
 *   hasError: boolean,
 *   refetch: Function,
 * }}
 */
const useServerStatus = ({
  serverInfo,
  serverId,
  serverPackageId,
  serverDomainStatus,
  forceRefetch = false,
}) => {
  const dispatch = useDispatch();

  // Função para forçar refetch
  const refetch = useCallback(() => {
    if (serverId) {
      dispatch(advancedHostingActions.serverStatus.request({ serverId }));
    }
  }, [dispatch, serverId]);

  // Busca o status específico deste servidor do estado mapeado
  const serverStatusState = useSelector(
    state => state.advancedHosting?.serverStatus?.byId?.[serverId],
    shallowEqual,
  );

  const isDedicatedVps = !HgProducts.VPS_OCI.includes(serverPackageId);

  const serverIsLocked = serverInfo?.locked;
  const serverIsLockedReason = serverInfo?.reason;

  let serverStatus = serverIsLocked ? serverIsLockedReason : serverStatusState?.status;
  serverStatus = serverStatus?.toLowerCase();

  const serverStatusLoading = serverIsLocked ? false : serverStatusState?.loading;
  const hasError = serverIsLocked ? false : !!serverStatusState?.error;
  const emptyStatus = !serverStatus;

  const serverDisabledFeatures = serverIsLocked
    || serverStatus === VpsOciStatusEnum.OFFLINE
    || serverStatus === VpsOciStatusEnum.BUILDING
    || serverStatus === VpsOciStatusEnum.PROCESSING
    || serverStatus === VpsOciStatusEnum.PENDING
    || serverStatus === VpsOciStatusEnum.SUSPENDED
    || serverStatus === VpsOciStatusEnum.REBUILD
    || (hasError && IS_PROD)
    || (emptyStatus && IS_PROD);
    // Foi necessário adicionar o IS_PROD, pois local e homologação nunca retorna o status.

  const alreadyFetched = serverStatusState !== undefined;

  useEffect(() => {
    if (
      serverId
      && (!alreadyFetched || forceRefetch)
      && !serverIsLocked // Se o servidor estiver locked, não busca status do WHMCS, pois o status é derivado do reason
      && !isDedicatedVps
    ) {
      dispatch(advancedHostingActions.serverStatus.request({ serverId }));
    }
  }, [dispatch, serverId, alreadyFetched, serverIsLocked, isDedicatedVps, forceRefetch]);

  if (isDedicatedVps) {
    return {
      serverStatus: serverIsLocked ? serverIsLockedReason : serverDomainStatus,
      serverStatusLoading: false,
      serverIsLocked,
      serverDisabledFeatures: !!serverIsLocked,
      hasError: false,
      refetch,
    };
  }

  return {
    serverStatus,
    serverStatusLoading,
    serverIsLocked,
    serverDisabledFeatures,
    hasError,
    refetch,
  };
};

export default useServerStatus;
